// World regions, for one job: keeping lists from bunching up.
//
// A ranking by weather alone is a ranking by latitude and coastline, and the
// city list is half China and India by count of large cities. So every list the
// resources pages print, and the choice of which cities get a page at all, is
// capped per region (see spread.ts and places.ts). The regions are coarse on
// purpose — eleven of them, the way a traveller divides the map, not a UN
// statistical scheme.
//
// Keyed by the iso2 codes present in cities.json. Excluded territories (see
// lib/cities.ts) are absent from the city list and so from this map; the test
// asserts every code the city list carries is mapped, so a rebuild that adds a
// country fails loudly rather than dropping it into no region.

export type RegionKey =
  | 'europe'
  | 'mena'
  | 'africa'
  | 'central-asia'
  | 'south-asia'
  | 'east-asia'
  | 'south-east-asia'
  | 'oceania'
  | 'north-america'
  | 'central-america'
  | 'south-america';

export const REGION_LABEL: Record<RegionKey, string> = {
  europe: 'Europe',
  mena: 'North Africa and the Middle East',
  africa: 'Africa south of the Sahara',
  'central-asia': 'Central Asia and the Caucasus',
  'south-asia': 'South Asia',
  'east-asia': 'East Asia',
  'south-east-asia': 'South-East Asia',
  oceania: 'Australia and the Pacific',
  'north-america': 'North America',
  'central-america': 'Mexico, Central America and the Caribbean',
  'south-america': 'South America',
};

// URL slugs for the region guides (/resources/regions/{slug}/). Short and
// stable; none is a country slug (resources-core.test.ts holds that).
export const REGION_SLUG: Record<RegionKey, string> = {
  europe: 'europe',
  mena: 'north-africa-and-middle-east',
  africa: 'sub-saharan-africa',
  'central-asia': 'central-asia-and-caucasus',
  'south-asia': 'south-asia',
  'east-asia': 'east-asia',
  'south-east-asia': 'south-east-asia',
  oceania: 'australia-and-pacific',
  'north-america': 'north-america',
  'central-america': 'mexico-central-america-and-caribbean',
  'south-america': 'south-america',
};

// Display order on the index pages: roughly west to east, north before south.
export const REGION_ORDER: RegionKey[] = [
  'north-america', 'central-america', 'south-america', 'europe', 'mena', 'africa',
  'central-asia', 'south-asia', 'east-asia', 'south-east-asia', 'oceania',
];

const MEMBERS: Record<RegionKey, string> = {
  europe: 'AD AL AT BA BE BG CH CY CZ DE DK EE ES FI FO FR GB GI GR HR HU IE IM IS IT JE LI LT LU LV MC MD ME MK MT NL NO PL PT RO RS SE SI SK SM UA VA XK',
  mena: 'AE BH DZ EG IQ JO KW LY MA OM QA SA SY TN TR YE XG XW',
  africa: 'AO BF BI BJ BW CD CF CG CI CM CV DJ ER ET GA GH GM GN GQ GW KE KM LR LS MG ML MR MU MW MZ NA NE NG RE RW SC SD SH SL SN SO SS ST SZ TD TG TZ UG YT ZA ZM ZW',
  'central-asia': 'AF AM AZ GE KG KZ TJ TM UZ',
  'south-asia': 'BD BT IN LK MV NP PK',
  'east-asia': 'CN HK JP KR MN MO TW',
  'south-east-asia': 'BN ID KH LA MM MY PH SG TH TL VN',
  oceania: 'AS AU CK FJ FM GU KI MH MP NC NU NZ PF PG PW SB TO TV VU WS',
  'north-america': 'BM CA GL US',
  'central-america': 'AG AW BB BQ BS BZ CR CU CW DM DO GD GP GT HN HT JM KN KY LC MF MQ MS MX NI PA PR SV SX TC TT VC VG',
  'south-america': 'AR BO BR CL CO EC FK GF GS GY PE PY SR UY VE',
};

export const REGION_OF_ISO2: ReadonlyMap<string, RegionKey> = new Map(
  (Object.entries(MEMBERS) as [RegionKey, string][]).flatMap(([region, codes]) => codes.split(' ').map((c) => [c, region] as const)),
);

export function regionOf(iso2: string | undefined): RegionKey | null {
  return (iso2 && REGION_OF_ISO2.get(iso2)) || null;
}

// The Atlantic islands that belong to European countries but not to the
// European climate. A "Europe in winter" list is honest to include them — they
// are Spain and Portugal — but not honest to present them as if they were the
// mainland, so their entries say where they are. Latitude alone separates them:
// nothing on the Iberian mainland lies south of 36°N.
export function islandNote(iso2: string | undefined, lat: number): string | null {
  if (lat >= 34) return null;
  if (iso2 === 'ES') return 'in the Canary Islands';
  if (iso2 === 'PT') return 'on Madeira';
  return null;
}

// Places the guides will not RECOMMEND: countries and territories under
// widespread "do not travel" advice from the main government travel advisories
// (the US State Department's level 4, the UK FCDO's "against all travel") at the
// time of writing — war, occupation, or collapse of basic safety. They keep
// their "best time to visit" country page, which is information, but they are
// left out of every ranking, every "where to go instead" suggestion, and the
// set of city guides. A ranking that sends a reader to a war zone for a city
// break is not an honest answer to the question, however good the weather.
//
// An editorial list, not data: review it when advice changes. Excluded
// territories (lib/cities.ts) are absent from the city list altogether.
export const NOT_RECOMMENDED_ISO2: ReadonlySet<string> = new Set([
  'AF', // Afghanistan
  'BF', // Burkina Faso
  'CF', // Central African Republic
  'HT', // Haiti
  'IQ', // Iraq
  'LY', // Libya
  'ML', // Mali
  'MM', // Myanmar
  'NE', // Niger
  'SD', // Sudan
  'SO', // Somalia
  'SS', // South Sudan
  'SY', // Syria
  'UA', // Ukraine
  'VE', // Venezuela
  'XG', // Gaza Strip
  'YE', // Yemen
]);

export const recommendable = (p: { iso2: string }): boolean => !NOT_RECOMMENDED_ISO2.has(p.iso2);
